import React, { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Button, Form, Modal, Alert } from 'react-bootstrap';
import { useAuth } from './context/AuthContext';
import { hasPermission } from './utils/permissions';
import { isSuperAdmin } from './utils/superAdmin';

const VisitsTable = () => {
  const { user } = useAuth();
  const [canEditRole, setCanEditRole] = useState(false);
  const [canDeleteVisit, setCanDeleteVisit] = useState(false);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'v_id', direction: 'desc' });
  const [showModal, setShowModal] = useState(false);
  const [editVisit, setEditVisit] = useState(null); // 用於編輯時的記錄
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // 檢測是否為手機端
  const [viewMode, setViewMode] = useState('auto'); // 顯示模式
  const [formData, setFormData] = useState({
    v_aid: '',
    v_name: '',
    v_sid: '',
    v_ssid: '',
    v_group: '',
    v_remark: '',
    v_date: '',
    v_edit_uid: user ? user.e_id : null,
    v_edit_day: new Date().toISOString().slice(0, 19).replace('T', ' '),
    v_status: 1,
  });

  const observer = useRef();
  const lastVisitRef = useRef();
  

      // 在 useEffect 中檢查權限
    useEffect(() => {
      const checkPermission = async () => {
        const editRolePermission = await hasPermission(user, 'EDIT_ROLE');
        setCanEditRole(editRolePermission);
        
        // 檢查刪除權限：e_id=80 或各社秘書(e_position=17)
        const canDelete = user && (
          isSuperAdmin(user.e_id) || 
          parseInt(user.e_position) === 17
        );
        setCanDeleteVisit(canDelete);
      };
      checkPermission();
    }, [user]);

  // 監聽視窗大小變化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 無限滾動邏輯
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (lastVisitRef.current) {
      observer.current.observe(lastVisitRef.current);
    }
  }, [loading, hasMore]);

  // 加載訪問記錄
  const fetchVisits = async (page, sortKey = 'v_id', sortDirection = 'desc', filter = '', isNewQuery = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/visits', {
        params: { filter, page, sortKey, sortDirection },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (isNewQuery) {
        setVisits(response.data); // 新查詢：重置資料
      } else {
        setVisits((prevVisits) => [...prevVisits, ...response.data]); // 分頁載入：累積資料
      }

      setHasMore(response.data.length > 0);
    } catch (error) {
      // Handle error silently
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchVisits(page, sortConfig.key, sortConfig.direction, filter);
  }, [page, filter, sortConfig]);

  // 排序功能
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setVisits([]);
    setPage(1);
    fetchVisits(1, key, direction, filter, true); // 新查詢
  };

  // 過濾功能
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setVisits([]);
    setPage(1);
    fetchVisits(1, sortConfig.key, sortConfig.direction, e.target.value, true); // 新查詢
  };

  // 開啟或關閉模態框
  const openModal = (visit = null) => {
    setEditVisit(visit);
    if (visit) {
      setFormData({ ...visit });
    } else {
      setFormData({
        v_aid: '',
        v_name: '',
        v_sid: '',
        v_ssid: '',
        v_group: '',
        v_remark: '',
        v_date: '',
        v_edit_uid: user ? user.e_id : null,
        v_edit_day: new Date().toISOString().slice(0, 19).replace('T', ' '),
        v_status: 1,
      });
    }
    setShowModal(true);
  };

  // 保存或更新訪問記錄
  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editVisit) {
        // 更新記錄
        await axios.put(`/visits/${editVisit.v_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessMessage("訪問記錄已成功更新！");
      } else {
        // 新增記錄
        const response = await axios.post('/visits', formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setVisits((prevVisits) => [...prevVisits, { ...formData, v_id: response.data.id }]);
        setSuccessMessage("訪問記錄已成功新增！");
      }
      setTimeout(() => setSuccessMessage(""), 3000);
      setShowModal(false);
    } catch (error) {
      // Handle error silently
    }
  };

  // 刪除訪問記錄
  const handleDelete = async (v_id) => {
    if (window.confirm('確定要刪除此訪問記錄嗎？')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/visits/${v_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setVisits((prevVisits) => prevVisits.filter((visit) => visit.v_id !== v_id));
        setSuccessMessage("訪問記錄已成功刪除！");
        setTimeout(() => setSuccessMessage(""), 3000);
      } catch (error) {
        let errorMsg = "刪除失敗";
        if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = `刪除失敗: ${error.message}`;
        }
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(""), 5000);
      }
    }
  };

  return (
    <div className="container-fluid mt-4">
      {/* 成功訊息 */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible>
          {successMessage}
        </Alert>
      )}
      
      {/* 錯誤訊息 */}
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage("")} dismissible>
          {errorMessage}
        </Alert>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <Form.Control
          type="text"
          placeholder="搜尋訪問記錄..."
          value={filter}
          style={{ flex: '1', minWidth: '200px' }}
          onChange={handleFilterChange}
        />
        <div className="d-flex align-items-center gap-2">
          {/* 視圖切換按鈕 */}
          <div className="btn-group btn-group-sm" role="group">
            <Button 
              variant={viewMode === 'auto' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('auto')}
              title="自動切換"
            >
              📱
            </Button>
            <Button 
              variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('table')}
              title="表格檢視"
            >
              📊
            </Button>
            <Button 
              variant={viewMode === 'card' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('card')}
              title="卡片檢視"
            >
              🃏
            </Button>
          </div>
          {canEditRole && (
            <Button variant="success" size="sm" onClick={() => openModal()}>
              新增
            </Button>
          )}
        </div>
      </div>

      {/* 資料顯示區域 - 根據模式切換 */}
      {(viewMode === 'card' || (viewMode === 'auto' && isMobile)) ? (
        /* 卡片式佈局 - 適合手機端 */
        <div className="row">
          {visits.map((visit, index) => (
            <div key={visit.v_id} className="col-12 col-md-6 col-lg-4 mb-3" ref={visits.length === index + 1 ? lastVisitRef : null}>
              <div className="card h-100 shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center bg-dark text-white">
                  <h6 className="mb-0">{visit.v_name}</h6>
                  <span className="badge bg-primary">#{visit.v_id}</span>
                </div>
                <div className="card-body">
                  <div className="row g-2 small">
                    <div className="col-6">
                      <strong>公司名稱:</strong><br/>
                      <span className="text-muted">{visit.company_name}</span>
                    </div>
                    <div className="col-6">
                      <strong>社別:</strong><br/>
                      <span className="text-muted">{visit.store_name}</span>
                    </div>
                    <div className="col-6">
                      <strong>日期:</strong><br/>
                      <span className="text-muted">{visit.v_date}</span>
                    </div>
                    <div className="col-6">
                      <strong>訪問名稱:</strong><br/>
                      <span className="text-muted">{visit.v_name}</span>
                    </div>
                    {visit.v_remark && (
                      <div className="col-12">
                        <strong>備註:</strong><br/>
                        <span className="text-muted small">{visit.v_remark}</span>
                      </div>
                    )}
                    {visit.activity_poster && (
                      <div className="col-12">
                        <strong>海報:</strong><br/>
                        <div style={{ 
                          position: 'relative',
                          width: '100%',
                          cursor: 'pointer'
                        }}>
                          <img
                            src={`/uploads/AEDM/${visit.activity_poster}?t=${new Date().getTime()}`}
                            alt="活動海報"
                            style={{
                              width: '100%',
                              height: 'auto',
                              aspectRatio: '0.71',
                              objectFit: 'contain',
                              borderRadius: '6px',
                              border: '1px solid #007bff',
                              backgroundColor: '#fff'
                            }}
                            onClick={() => {
                              setLightboxImage(`/uploads/AEDM/${visit.activity_poster}?t=${new Date().getTime()}`);
                              setIsLightboxOpen(true);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {(canEditRole || canDeleteVisit) && (
                  <div className="card-footer bg-transparent">
                    <div className="d-flex gap-2">
                      {canEditRole && (
                        <Button 
                          variant="info" 
                          size="sm" 
                          className="flex-fill"
                          onClick={() => openModal(visit)}
                        >
                          編輯
                        </Button>
                      )}
                      {canDeleteVisit && (
                        <Button 
                          variant="danger" 
                          size="sm" 
                          className="flex-fill"
                          onClick={() => handleDelete(visit.v_id)}
                        >
                          刪除
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="col-12 text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">加載中...</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* 表格式佈局 - 適合桌面端 */
        <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover variant="dark" className="table-striped table-dark" style={{ 
          tableLayout: 'auto',
          minWidth: '800px'
        }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('v_id')} style={{ 
                width: '60px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>編號</th>
              <th onClick={() => handleSort('company_name')} style={{ 
                minWidth: '150px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>公司名稱</th>
              <th onClick={() => handleSort('store_name')} style={{ 
                width: '100px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>社別</th>
              <th onClick={() => handleSort('v_name')} style={{ 
                minWidth: '120px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>訪問名稱</th>
              <th onClick={() => handleSort('v_date')} style={{ 
                width: '100px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>日期</th>
              <th onClick={() => handleSort('v_remark')} style={{ 
                minWidth: '120px', 
                cursor: 'pointer',
                fontSize: '14px'
              }}>備註</th>
              <th style={{ 
                width: '120px',
                fontSize: '14px'
              }}>海報</th>
              {(canEditRole || canDeleteVisit) && (
                <th style={{ 
                  width: '80px',
                  fontSize: '14px'
                }}>操作</th>
              )}
            </tr>
          </thead>
        <tbody>
          {visits.map((visit, index) => (
            <tr key={visit.v_id} ref={visits.length === index + 1 ? lastVisitRef : null}>
              <td style={{ textAlign: 'center', fontSize: '14px' }}>{visit.v_id}</td>
              <td style={{ 
                wordBreak: 'break-word', 
                overflow: 'hidden',
                fontSize: '14px',
                padding: '8px'
              }}>
                {visit.company_name}
              </td>
              <td style={{ 
                textAlign: 'center', 
                fontSize: '13px',
                padding: '8px'
              }}>
                {visit.store_name}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                fontSize: '14px',
                padding: '8px'
              }}>
                {visit.v_name}
              </td>
              <td style={{ 
                textAlign: 'center', 
                fontSize: '13px',
                padding: '8px'
              }}>
                {visit.v_date}
              </td>
              <td style={{ 
                wordBreak: 'break-word', 
                overflow: 'hidden',
                fontSize: '13px',
                padding: '8px',
                maxHeight: '60px'
              }}>
                {visit.v_remark}
              </td>
              <td className="poster-container" style={{ 
                textAlign: 'center', 
                padding: '4px',
                minWidth: '100px',
                maxWidth: '150px'
              }}>
                {visit.activity_poster ? (
                  <div style={{ 
                    position: 'relative',
                    width: '100%',
                    cursor: 'pointer'
                  }}>
                    <img
                      src={`/uploads/AEDM/${visit.activity_poster}?t=${new Date().getTime()}`}
                      alt="活動海報"
                      style={{
                        width: '100%',
                        height: 'auto',
                        aspectRatio: '0.71', // A4 比例 (210:297)
                        objectFit: 'contain',
                        borderRadius: '6px',
                        border: '1px solid #007bff',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        backgroundColor: '#fff'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                      }}
                      onClick={() => {
                        setLightboxImage(`/uploads/AEDM/${visit.activity_poster}?t=${new Date().getTime()}`);
                        setIsLightboxOpen(true);
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      backgroundColor: 'rgba(0, 123, 255, 0.9)',
                      color: 'white',
                      borderRadius: '12px',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      backdropFilter: 'blur(4px)'
                    }}>
                      🔍
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    aspectRatio: '0.71',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px dashed #dee2e6',
                    color: '#6c757d',
                    fontSize: '11px',
                    minHeight: '60px'
                  }}>
                    無海報
                  </div>
                )}
              </td>
              {(canEditRole || canDeleteVisit) && ( 
                <td style={{ 
                  textAlign: 'center', 
                  padding: '8px',
                  whiteSpace: 'nowrap'
                }}>
                  {canEditRole && (
                    <Button 
                      variant="info" 
                      size="sm" 
                      style={{ marginRight: '4px', fontSize: '12px' }}
                      onClick={() => openModal(visit)}
                    >
                      編輯
                    </Button>
                  )}
                  {canDeleteVisit && (
                    <Button 
                      variant="danger" 
                      size="sm" 
                      style={{ fontSize: '12px' }}
                      onClick={() => handleDelete(visit.v_id)}
                    >
                      刪除
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {loading && (
            <tr>
              <td colSpan={(canEditRole || canDeleteVisit) ? 8 : 7} className="text-center">
                加載中...
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>
      )}

      {/* 新增/編輯模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editVisit ? "編輯訪問記錄" : "新增訪問記錄"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>訪問名稱</Form.Label>
              <Form.Control
                type="text"
                value={formData.v_name}
                onChange={(e) => setFormData({ ...formData, v_name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>公司名稱</Form.Label>
              <Form.Control
                type="text"
                value={formData.v_sid}
                onChange={(e) => setFormData({ ...formData, v_sid: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>社別</Form.Label>
              <Form.Control
                type="text"
                value={formData.v_ssid}
                onChange={(e) => setFormData({ ...formData, v_ssid: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>日期</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.v_date}
                onChange={(e) => setFormData({ ...formData, v_date: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>備註</Form.Label>
              <Form.Control
                as="textarea"
                value={formData.v_remark}
                onChange={(e) => setFormData({ ...formData, v_remark: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSave}>
            儲存
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 海報 Lightbox */}
      {isLightboxOpen && (
        <div 
          style={{
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            backgroundColor: 'rgba(0, 0, 0, 0.9)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1050,
            backdropFilter: 'blur(5px)'
          }}
          onClick={() => setIsLightboxOpen(false)}
        >
          <div style={{ position: 'relative' }}>
            <img 
              src={lightboxImage} 
              alt="海報 Lightbox"
              style={{ 
                maxWidth: '90vw', 
                maxHeight: '90vh', 
                objectFit: 'contain',
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                border: '3px solid white'
              }}
            />
            <button
              style={{
                position: 'absolute',
                top: '-15px',
                right: '-15px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: '#ff4757',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              onClick={(e) => {
                e.stopPropagation();
                setIsLightboxOpen(false);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitsTable;
